import React, { useState, useEffect } from "react";
import { message } from "antd";
import { useTheme } from "../../../ThemeContext";
import { getStaffById, updateStaffById, getCurrentStaffId } from "../onboarding-services";
import { isValidUSPhoneNumber, formatPhoneNumber, formatPhoneNumberForEdit } from "../../../utils/helpers/format-phone-number";
import "./StepStyles.css";

const AdminDetailsStep = ({ onNext, onPrevious, isLoading, setIsLoading }) => {
  const { themeMode } = useTheme();
  
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: ""
  });
  
  const [errors, setErrors] = useState({});
  const [hasLoadedData, setHasLoadedData] = useState(false);

  // Load existing staff data on component mount
  useEffect(() => {
    loadStaffData();
  }, []);

  const loadStaffData = async () => {
    try {
      setIsLoading(true);
      
      // Wait for API key to be available in session storage
      let attempts = 0;
      const maxAttempts = 10; // Max 3 seconds wait
      
      while (attempts < maxAttempts) {
        const apiKey = sessionStorage.getItem("api_key");
        if (apiKey) {
          console.log("🔑 API key found, proceeding with staff data load");
          break;
        }
        console.log(`⏳ Waiting for API key... Attempt ${attempts + 1}/${maxAttempts}`);
        await new Promise(resolve => setTimeout(resolve, 300)); // Wait 300ms
        attempts++;
      }
      
      const apiKey = sessionStorage.getItem("api_key");
      if (!apiKey) {
        console.warn("⚠️ API key not available after waiting, proceeding anyway");
      }
      
      const staffId = getCurrentStaffId();
      
      if (!staffId) {
        throw new Error("Staff ID not found");
      }
      
      const response = await getStaffById(staffId);
      
      if (response.statusCode === "SUCCESS" && response.response) {
        const staff = response.response;
        setFormData({
          name: staff.name || "",
          phoneNumber: formatPhoneNumberForEdit(staff.phoneNumber) || "",
          email: staff.email || ""
        });
      }
      setHasLoadedData(true);
    } catch (error) {
      console.error("Error loading staff data:", error);
      message.error("Failed to load admin details");
      setHasLoadedData(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Special handling for phone number to provide basic formatting
    if (field === 'phoneNumber') {
      // Remove the +1 prefix if user tries to modify it
      const valueWithoutPrefix = value.replace(/^\+1\s*/, '');
      
      // Remove all non-digit characters for processing
      const cleaned = valueWithoutPrefix.replace(/\D/g, '');
      
      // Format as (XXX) XXX-XXXX if we have enough digits
      let formatted = '';
      if (cleaned.length === 0) {
        formatted = '';
      } else if (cleaned.length <= 3) {
        formatted = cleaned;
      } else if (cleaned.length <= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
      
      setFormData(prev => ({ ...prev, [field]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle key events for phone number input
  const handlePhoneKeyDown = (event) => {
    const input = event.target;
    const cursorPosition = input.selectionStart || 0;
    
    // Prevent deletion or modification of +1 prefix
    if ((event.key === 'Backspace' || event.key === 'Delete') && cursorPosition <= 3) {
      event.preventDefault();
      return;
    }
    
    // Prevent moving cursor into the +1 prefix with arrow keys
    if (event.key === 'ArrowLeft' && cursorPosition <= 3) {
      event.preventDefault();
      return;
    }
    
    // Prevent Home key from going to beginning of +1
    if (event.key === 'Home') {
      event.preventDefault();
      setTimeout(() => {
        if (input) {
          input.setSelectionRange(3, 3);
        }
      }, 0);
      return;
    }
    
    // Only allow numbers and navigation keys
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Tab', 'Enter', 'Escape'
    ];
    
    // Allow Ctrl+A, Ctrl+C, Ctrl+V, etc.
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    
    if (!allowedKeys.includes(event.key) && !/\d/.test(event.key)) {
      event.preventDefault();
    }
  };

  // Handle click events for phone number input
  const handlePhoneClick = (event) => {
    const input = event.target;
    setTimeout(() => {
      const cursorPosition = input.selectionStart || 0;
      if (cursorPosition < 3) {
        input.setSelectionRange(3, 3);
      }
    }, 0);
  };

  // Handle focus events for phone number input
  const handlePhoneFocus = (event) => {
    const input = event.target;
    setTimeout(() => {
      // Set cursor to after +1 prefix if the field is empty or cursor is in prefix
      if (!formData.phoneNumber || input.selectionStart < 3) {
        input.setSelectionRange(3, 3);
      }
    }, 0);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!isValidUSPhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = "Enter a valid US phone number";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      const staffId = getCurrentStaffId();
      
      if (!staffId) {
        throw new Error("Staff ID not found");
      }

      const response = await updateStaffById(staffId, {
        name: formData.name,
        phoneNumber: formatPhoneNumber(formData.phoneNumber),
        email: formData.email
      });
      
      if (response.statusCode === "SUCCESS") {
        message.success("Admin details saved successfully!");
        onNext();
      } else {
        message.error(response.message || "Failed to save admin details");
      }
    } catch (error) {
      console.error("Error saving admin details:", error);
      message.error("Failed to save admin details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasLoadedData) {
    return (
      <div className="step-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin details...</p>
      </div>
    );
  }

  return (
    <div className={`step-container ${themeMode}`}>
      <div className="step-form">
        {/* Admin Details Section */}
        <div className="form-section">
          <div className="form-row">
            <div className="form-field">
              <label className="form-label">Name*</label>
              <input
                type="text"
                placeholder="Enter Customer Name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`form-input ${errors.name ? "error" : ""}`}
                disabled={isLoading}
              />
              <div className="form-error-container">
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Phone Number*</label>
              <input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.phoneNumber ? `+1 ${formData.phoneNumber}` : "+1 "}
                onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                className={`form-input ${errors.phoneNumber ? "error" : ""}`}
                disabled={isLoading}
                onKeyDown={(e) => handlePhoneKeyDown(e)}
                onClick={(e) => handlePhoneClick(e)}
                onFocus={(e) => handlePhoneFocus(e)}
              />
              <div className="form-error-container">
                {errors.phoneNumber && <span className="form-error">{errors.phoneNumber}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Email ID*</label>
              <input
                type="email"
                placeholder="Enter Email ID"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`form-input ${errors.email ? "error" : ""}`}
                disabled={isLoading}
              />
              <div className="form-error-container">
                {errors.email && <span className="form-error">{errors.email}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="step-actions has-previous">
          <button
            className="btn-previous"
            onClick={onPrevious}
            disabled={isLoading}
          >
            Previous
          </button>
          <button
            className="btn-next"
            onClick={handleNext}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save & Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDetailsStep; 